import Vue from 'vue'
import Cookies from 'js-cookie'

export const TIMEZONES = [
  { label: 'Universal Coordinated Time (UTC)', value: 'UTC' },
  { label: 'Eastern Time (Washington, DC)', value: 'America/New_York' },
  { label: 'Central Time (Chicago)', value: 'America/Chicago' },
  { label: 'Mountain Time (Salt Lake City)', value: 'America/Denver' },
  { label: 'Arizona Time (Phoenix)', value: 'America/Phoenix' },
  { label: 'Pacific Time (Los Angeles)', value: 'America/Los_Angeles' },
  { label: 'Alaskan Time (Anchorage)', value: 'America/Anchorage' },
  { label: 'Hawaii-Aleutian Time (Honolulu)', value: 'Pacific/Honolulu' }
]

function snAdminCanAccess (toRoute) {
  const viewAsCompanyGuid = sessionStorage.getItem('srm_view_as_company_guid')
  switch (toRoute) {
    case 'Portal logged out':
    case 'Page Not Found':
    case 'DevTools':
    case 'Integrations':
    case 'Companies':
    case 'Company Details':
    case 'Portal':
    case 'SSO':
      return true
    default:
      return viewAsCompanyGuid != null
  }
}

export default {
  getDataGroup (label, value) {
    if (value == null || value === '') {
      return null
    }
    return {
      label: label,
      value: value
    }
  },

  addDataGroup (list, label, value) {
    const dataGroup = this.getDataGroup(label, value)
    if (dataGroup != null) {
      list.push(dataGroup)
    }
  },

  formatDate (date) {
    const today = Vue.prototype.$moment(new Date()).format('MMM DD, YYYY')
    const calcTomorrow = new Date()
    calcTomorrow.setDate(calcTomorrow.getDate() + 1)
    const tomorrow = Vue.prototype.$moment(calcTomorrow).format('MMM DD, YYYY')

    const isToday = date === today
    const isTomorrow = date === tomorrow
    let label = ''

    if (isToday) {
      label = `Today, ${date}`
    } else if (isTomorrow) {
      label = `Tomorrow, ${date}`
    } else {
      label = date
    }
    return { label: label, isToday: isToday, isTomorrow: isTomorrow }
  },

  formatLocalTimestamp (timestamp) {
    const datetime = new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
    return datetime.replace(/^(.+?,.+?),\s*/g, '$1 ') // removes commas after first instance
  },

  formatDateTimezone (timestamp, timezone) {
    const tzLabel = TIMEZONES.find(tz => tz.label === timezone)
    const tz = tzLabel ? tzLabel.value : TIMEZONES[0].value // default to UTC
    const datetime = new Date(timestamp).toLocaleString('en-US', {
      timeZone: tz,
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    })
    return datetime.replace(/^(.+?,.+?),\s*/g, '$1 ') // removes commas after first instance
  },

  formatTimestamp (timestamp, timezone) {
    const tzLabel = TIMEZONES.find(tz => tz.label === timezone)
    const tz = tzLabel ? tzLabel.value : TIMEZONES[0].value // default to UTC
    const datetime = new Date(timestamp).toLocaleString('en-US', {
      timeZone: tz,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
    return datetime.replace(/^(.+?,.+?),\s*/g, '$1 ') // removes commas after first instance
  },

  toggleVerticalScrollbar (hideVerticalScrollbar) { // to fix double scrollbar when in captive flow (INV-1610)
    document.getElementsByTagName('html')[0].style.overflowY = hideVerticalScrollbar ? 'hidden' : ''
  },

  validateEmail (email) {
    return email
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
  },

  processDocumentData (base64String, fileType, openInNewTab) {
    // Decode base64 string
    const decodedData = window.atob(base64String)

    // Create uInt8Array of size same as row data length
    const uInt8Array = new Uint8Array(decodedData.length)

    // Insert all character code into uInt8Array
    for (let i = 0; i < decodedData.length; i++) {
      uInt8Array[i] = decodedData.charCodeAt(i)
    }

    // Return BLOB image after conversion
    const blob = new Blob([uInt8Array], { type: fileType })

    if (openInNewTab) {
      // Create URL and open in new tab
      const url = URL.createObjectURL(blob)

      window.open(url, '_blank')
    } else {
      return blob
    }
  },

  hasPermission (user, action) {
    if (user.isSnAdmin) {
      return true
    } else if (user == null || !user.permissions) {
      return false
    }
    return user.permissions[action]
  },

  canAccessSettings (user) {
    return user != null && user.permissions != null && (user.permissions.manageRoles || user.permissions.manageUsers || user.permissions.manageOrgs)
  },

  requiresOrg (toRoute) {
    return ['Session Details'].includes(toRoute)
  },

  getDefaultPage (user) {
    if (!user.loggedIn) {
      return '/manager/login'
    } else if (user.isSnAdmin) {
      return '/manager/companies'
    } else if (user.locked || user.companyLocked) {
      return `/no_access/${user.companyLocked ? 'company_locked' : 'user_locked'}`
    } else if (user.permissions.manageSigningSessions) {
      return '/manager/sessions'
    } else if (this.canAccessSettings(user)) {
      return '/manager/settings'
    } else if (user.permissions.manageReports) {
      return '/manager/reports'
    }
    return '/no_access/no_permissions'
  },

  canAccess (user, toRoute) {
    if (toRoute === 'Manager Blank Page') {
      return user.loading
    }
    if (user != null && user.email != null && user.email.includes('integration') && user.isSnAdmin) {
      return true
    }
    if (user != null && user.isSnAdmin) {
      return snAdminCanAccess(toRoute)
    }

    if ([
      'Login',
      'SSO',
      'Portal',
      'Portal logged out',
      'Page Not Found'
    ].includes(toRoute)) {
      return true
    } else if ([
      'Session Details',
      'Signing sessions'
    ].includes(toRoute)) {
      return user.permissions.manageSigningSessions
    } else if ([
      'DevTools',
      'Companies',
      'Company Details'
    ].includes(toRoute)) {
      return false // only snAdmins can access these pages which is handled outside the switch statement
    } else if (toRoute === 'Signer room settings') {
      return user.permissions.manageSigners
    } else if (toRoute === 'Settings') {
      return user.permissions.manageUsers ||
        user.permissions.manageRoles ||
        user.permissions.manageOrgs
    } else if (toRoute === 'Reports') {
      return user.permissions.manageReports
    } else if (toRoute === 'No Access' || toRoute === 'Consent') {
      return user.loggedIn
    } else {
      return false
    }
  },

  formattedMassUsers (users, compareGuids, roleGuid = null, orgGuid = null) {
    const _users = []
    users.map(user => {
      _users.push({ key: user.guid, value: `${user.name} - ${user.email}`, disabled: compareGuids.includes(user.guid) || user.roleGuid === roleGuid || user.orgGuids?.includes(orgGuid) })
    })
    return _users
  },

  formattedMassOrgs (orgs, compareGuids) {
    const formattedOrgs = []
    orgs.map(org => {
      formattedOrgs.push({ key: org.guid, value: org.name, disabled: compareGuids.includes(org.guid) })
    })
    return formattedOrgs
  },

  checkForSelfReload (userGuids, loggedInUser) {
    let reload = false
    userGuids.forEach(userGuid => {
      if (userGuid === loggedInUser.guid) {
        setTimeout(() => {
          window.location.reload()
        }, 3000) // delay 3 seconds with toast acknowledging application of change
        reload = true
      }
    })
    return reload
  },

  redirectToPortal (sessionGuid, signerGuid, orgId, returnUrl, source) {
    Cookies.set('srm_preview_session_guid', sessionGuid)
    if (signerGuid == null) {
      Cookies.remove('srm_signer_guid')
    } else {
      Cookies.set('srm_signer_guid', signerGuid)
    }
    Cookies.set('srm_org_id', orgId)
    this.setReturnCookieData(returnUrl, source)
    window.location.href = '/preview_portal'
  },

  setReturnCookieData (returnUrl, source) {
    Cookies.set('srm_redirect_url', returnUrl)
    const data = {
      return_url: returnUrl,
      guid: null,
      source: source
    }
    Cookies.set('srm_redirect_data', btoa(JSON.stringify(data)))
  },

  returnToBorrowerPortal (eventType) {
    const redirect_url = Cookies.get('srm_redirect_url')
    if (redirect_url == null) {
      sessionStorage.clear()
      window.location.assign('/logout')
      return
    }
    let url = `${redirect_url}?event_type=${eventType}`
    const data = Cookies.get('srm_redirect_data')
    if (data != null && data !== '') {
      url += `&state=${data}`
    }
    window.location.href = url
  },

  getElementMargins (el, marginTypes) {
    let margins = 0
    marginTypes.forEach(marginType => {
      margins += parseFloat(getComputedStyle(el)[marginType])
    })
    return margins
  },

  getAbsoluteElementHeight (el) {
    const margins = this.getElementMargins(el, ['margin-top', 'margin-bottom'])
    return Math.ceil(el.offsetHeight + margins)
  }
}
